/**
 * An expansion option's own figures — build 6B.
 *
 * Every option on the capacity desk ran on starting figures — a cost, a build
 * time, the room it adds — that nobody at the airport could replace, so the
 * cost per passenger and the start-by year never changed. Here they are
 * entered per option, or the option is left out of the comparison; "Use
 * starting figures" takes the airport's own back out. Kept in the desk's
 * note with who and when.
 */
import { useState } from "react";
import { Button, Field, Input, Segmented } from "../../design/components";
import { optionRefusal, setOptionFigures, type OptionFigures } from "../../domain/executiveEntries";
import type { ExpansionEntered, ExpansionOption } from "../../domain/expansion";
import { t } from "../../i18n";
import s from "./EntryForms.module.css";

const num = (v: string) => (v.trim() === "" ? undefined : Number(v));

export function OptionFiguresForm({
  option,
  entered,
  onSave,
  onDone,
}: {
  option: ExpansionOption;
  entered: ExpansionEntered;
  onSave: (next: ExpansionEntered) => Promise<void> | void;
  onDone: () => void;
}) {
  const own = entered.options?.[option.key];
  const [cost, setCost] = useState(String(own?.cost ?? option.cost));
  const [months, setMonths] = useState(String(own?.months ?? option.months));
  const [capacity, setCapacity] = useState(String(own?.capacityPax ?? option.capacityPax));
  const [stands, setStands] = useState(String(own?.stands ?? option.stands));
  const [use, setUse] = useState<"in" | "out">(own?.enabled === false ? "out" : "in");
  const [why, setWhy] = useState<string | null>(null);

  const save = async () => {
    const figures: OptionFigures = {
      cost: num(cost),
      months: num(months),
      capacityPax: num(capacity),
      stands: num(stands),
      enabled: use === "in",
    };
    const refused = optionRefusal(figures);
    if (refused) return setWhy(refused);
    setWhy(null);
    onDone();
    await onSave(setOptionFigures(entered, option.key, figures));
  };

  return (
    <div className={s.form} data-testid={`option-form-${option.key}`}>
      <Field label="Cost">
        <Input type="number" min={0} step="any" value={cost} onChange={(e) => setCost(e.target.value)} />
      </Field>
      <Field label="Build time (months)">
        <Input type="number" min={1} max={240} step={1} value={months} onChange={(e) => setMonths(e.target.value)} />
      </Field>
      <Field label="Passengers a year it adds">
        <Input type="number" min={0} step="any" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
      </Field>
      <Field label="Stands it adds">
        <Input type="number" min={0} step={1} value={stands} onChange={(e) => setStands(e.target.value)} />
      </Field>
      <div className={s.wide}>
        <Segmented
          ariaLabel="In the comparison"
          value={use}
          onChange={setUse}
          options={[
            { value: "in", label: "Compare it" },
            { value: "out", label: "Leave it out" },
          ]}
        />
      </div>
      {why && (
        <p className={`${s.err} ${s.wide}`} role="alert">
          {t(why)}
        </p>
      )}
      <div className={`${s.actions} ${s.wide}`}>
        <Button variant="primary" onClick={() => void save()}>
          {t("Save the figures")}
        </Button>
        {own && (
          <Button
            variant="ghost"
            onClick={async () => {
              onDone();
              await onSave(setOptionFigures(entered, option.key, null));
            }}
          >
            {t("Use starting figures")}
          </Button>
        )}
        <Button variant="ghost" onClick={onDone}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
